/**
 * Redirect to an upstream Open edX surface we have not taken over.
 *
 * This component is the incremental-migration safety net. Any surface marked
 * `external` in config sends the user to the existing app instead of 404ing, so
 * the thin app can go live while it is still partial. The list of external
 * surfaces shrinking over time is the project's progress metric.
 *
 * Some surfaces stay external permanently by design — authoring, ORA grading, the
 * instructor dashboard — and that is a decision, not a gap.
 */

import { useEffect } from 'react';
import { getConfig } from '../lib/config';
import { lmsOrigin } from '../lib/openedx';

/** Where each upstream surface actually lives, relative to its host app. */
const UPSTREAM: Record<string, { host: 'lms' | 'studio'; path: string }> = {
  authoring: { host: 'studio', path: '/home' },
  gradebook: { host: 'lms', path: '/gradebook' },
  ora_grading: { host: 'lms', path: '/ora-grading' },
  instructor_dashboard: { host: 'lms', path: '/instructor' },
  discussions: { host: 'lms', path: '/discussions' },
};

export function ExternalSurface({ surface }: { surface: string }) {
  const config = getConfig();
  const target = UPSTREAM[surface];

  useEffect(() => {
    if (!target) return;
    // Studio has its own hostname, which the tenant record carries; everything else
    // is the LMS origin this build was pointed at.
    const origin =
      target.host === 'studio' && config.domains.studio
        ? `${window.location.protocol}//${config.domains.studio}`
        : lmsOrigin;
    window.location.replace(`${origin}${target.path}`);
  }, [surface, target, config]);

  if (!target) {
    return (
      <main className="page page--message">
        <h1>Not available yet</h1>
        <p>This part of {config.tenant.name} has not moved over yet.</p>
      </main>
    );
  }

  return (
    <main className="page page--message" aria-live="polite">
      <p>Taking you to {config.tenant.name}&hellip;</p>
    </main>
  );
}
